import { useEffect } from "react";
import hotkeys, { type HotkeysEvent } from "hotkeys-js";

import { isMac } from "fond/utils/device";

/**
 * Hardcoded map of supported hotkeys in the application.
 * This map can later be extended to allow user customization.
 */
export const HOTKEY_MAP = {
  UNDO: "ctrl+z, command+z",
  REDO: "ctrl+y, ctrl+shift+z, command+shift+z",
  DELETE: "delete, backspace",
  PHASE_A: "a",
  PHASE_B: "b",
  PHASE_C: "c",
  SEARCH: "/",
  SPLIT: "ctrl+shift+x, command+shift+x",
  ESCAPE: "escape",
} as const;

export type HotkeyAction = keyof typeof HOTKEY_MAP;

/**
 * Formats a hotkey string for display.
 * Converts internal format (e.g., "ctrl+z") to display format (e.g., "Ctrl+Z" or "⌘Z")
 */
const formatHotkeyForDisplay = (hotkey: string, useMacSymbols = false): string => {
  const parts = hotkey.split("+");
  const mac = isMac();
  const formatted = parts.map((part) => {
    const lower = part.toLowerCase().trim();

    // Mac-specific formatting (only use symbols if on Mac)
    if (useMacSymbols && mac) {
      if (lower === "command" || lower === "cmd") return "⌘";
      if (lower === "ctrl" || lower === "control") return "⌃";
      if (lower === "shift") return "⇧";
      if (lower === "alt" || lower === "option") return "⌥";
    }

    // Standard formatting
    if (lower === "command" || lower === "cmd") return "Cmd";
    if (lower === "ctrl" || lower === "control") return "Ctrl";
    if (lower === "shift") return "Shift";
    if (lower === "alt") return "Alt";
    if (lower === "option") return "Option";
    if (lower === "delete") return "DEL";
    if (lower === "backspace") return "BACKSPACE";
    if (lower === "escape" || lower === "esc") return "ESC";

    // Capitalize first letter for other keys
    return part.charAt(0).toUpperCase() + part.slice(1);
  });

  return formatted.join(useMacSymbols && mac ? "" : "+");
};

/**
 * Gets a human-readable label for a hotkey action based on the current platform.
 *
 * @param action - The hotkey action (e.g., 'UNDO', 'DELETE')
 * @param useMacSymbols - Whether to use Mac symbols (⌘, ⌃, etc.) instead of text. Default: false
 * @returns A formatted string like "Ctrl+Z" on Windows/Linux or "Cmd+Z" on Mac
 *
 * @example
 * ```tsx
 * getHotkeyLabel('UNDO') // Returns "Ctrl+Z" on Windows, "Cmd+Z" on Mac
 * getHotkeyLabel('UNDO', true) // Returns "Ctrl+Z" on Windows, "⌘Z" on Mac
 * getHotkeyLabel('DELETE') // Returns "DEL"
 * ```
 */
export const getHotkeyLabel = (action: HotkeyAction, useMacSymbols = false): string => {
  const hotkeyString = HOTKEY_MAP[action];
  const combinations = hotkeyString.split(",").map((s) => s.trim());

  const mac = isMac();

  // Find the appropriate combination for the platform
  for (const combo of combinations) {
    const lower = combo.toLowerCase();
    const isCommandCombo = lower.includes("command") || lower.includes("cmd");
    const isCtrlCombo = lower.includes("ctrl");

    // On Mac, prefer command combos; on others, prefer ctrl combos
    if (mac && isCommandCombo) {
      return formatHotkeyForDisplay(combo, useMacSymbols);
    }
    if (!mac && isCtrlCombo) {
      return formatHotkeyForDisplay(combo, useMacSymbols);
    }
  }

  // Fallback to the first combination if no platform-specific one found
  return formatHotkeyForDisplay(combinations[0], useMacSymbols);
};

interface UseHotkeysOptions {
  /**
   * The hotkey action to listen for (e.g., 'UNDO', 'DELETE'). No hotkey is registered when undefined.
   */
  action?: HotkeyAction;
  /**
   * Callback function to execute when the hotkey is triggered
   */
  callback: (event: KeyboardEvent, handler: HotkeysEvent) => void | boolean;
  /**
   * Whether the hotkey is currently enabled
   * @default true
   */
  enabled?: boolean;
  /**
   * Scope for the hotkey (useful for isolating hotkeys to specific contexts)
   * @default 'all'
   */
  scope?: string;
}

/**
 * Hook to register keyboard shortcuts using the centralized hotkey map.
 *
 * @example
 * ```tsx
 * useHotkeys({
 *   action: 'UNDO',
 *   callback: (event) => {
 *     event.preventDefault();
 *     undo();
 *   },
 *   enabled: canUndo,
 * });
 * ```
 */
export const useHotkeys = ({ action, callback, enabled = true, scope = "all" }: UseHotkeysOptions) => {
  useEffect(() => {
    if (!enabled || !action) {
      return;
    }

    const hotkeyString = HOTKEY_MAP[action];

    // Register the hotkey
    hotkeys(hotkeyString, scope, callback);

    // Cleanup: unbind the hotkey when component unmounts or dependencies change
    // eslint-disable-next-line consistent-return
    return () => {
      hotkeys.unbind(hotkeyString, scope);
    };
  }, [action, callback, enabled, scope]);
};
